// /admin/ai-agents — staff dashboard for AI-editor agents. Lists agents,
// lets you create/edit/toggle, view recent decisions + prompt history,
// manage cost caps + per-community settings.
//
// Not a fancy UI — functional table + prompt textareas + JSON output.
// Once we have real usage patterns we can polish.

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';

interface Agent {
    ID:                 string;    // Go emits PascalCase without json tags
    Name:               string;
    KCUsername:         string;
    OnHereUserID:       string;
    CommunityID:        string;
    CommunitySlug:      string;
    SystemPrompt:       string;
    CommentPrompt?:     string | null;
    ApproveStyle:       'suggest_only' | 'auto';
    ModerateComments:   boolean;
    Model:              string;
    MaxTokens:          number;
    Temperature:        number;
    PollIntervalS:      number;
    Active:             boolean;
    PausedReason?:      string | null;
    LastPolledAt?:      string | null;
    Role?:              'editor' | 'reporter' | 'writer' | 'curator' | 'weather' | 'feed_writer';
}

interface Decision {
    ID:           number;
    SubjectType:  string;
    SubjectID:    string;
    DecisionKind: string;
    Reasoning:    string;
    Executed:     boolean;
    Model:        string;
    TokensIn:     number;
    TokensOut:    number;
    CostCents:    number;
    LatencyMS:    number;
    CreatedAt:    string;
}

interface CostSummary {
    global: { daily_cents: number; monthly_cents: number; cap_daily: number; cap_monthly: number };
    agents: Array<{
        agent_id: string; agent_name: string; community: string;
        daily_cents: number; monthly_cents: number;
        cap_daily: number; cap_monthly: number; cap_scope: string;
        active: boolean; paused_reason?: string | null;
    }>;
}

export function AIAgents() {
    const api = useApi();
    const { authed, login } = useAuth();
    const [agents, setAgents] = useState<Agent[]>([]);
    const [cost, setCost] = useState<CostSummary | null>(null);
    const [selected, setSelected] = useState<string | null>(null);
    const [forbidden, setForbidden] = useState(false);

    const load = useCallback(async () => {
        if (!authed) return;
        try {
            const [a, c] = await Promise.all([
                api.get<{ agents: Agent[] }>('/api/admin/ai-agents'),
                api.get<CostSummary>('/api/admin/ai-cost'),
            ]);
            setAgents(a.agents ?? []);
            setCost(c);
        } catch (e) {
            if (e instanceof ApiError && e.status === 403) { setForbidden(true); return; }
            console.error('ai-agents load', e);
        }
    }, [api, authed]);
    useEffect(() => { load(); }, [load]);

    if (!authed) return (
        <div className="prelogin">
            <p>Log in.</p>
            <button type="button" className="plain" onClick={login}>Log in</button>
        </div>
    );
    if (forbidden) return (
        <main className="paper-wrap" style={{ padding: 40 }}>
            <h1>Staff only</h1>
            <p>You need <code>ONHERE_ADMIN_EMAILS</code> to see this page.</p>
        </main>
    );

    const [activityFor, setActivityFor] = useState<string | null>(null);
    const selectedAgent = agents.find(a => a.ID === selected);
    const activityAgent = agents.find(a => a.ID === activityFor);

    return (
        <main className="paper-wrap" style={{ padding: '20px 24px 60px', maxWidth: 1100 }}>
            <div className="masthead">
                <div className="rule-top" />
                <h1>AI Agents</h1>
                <div className="issue">Manage per-community agents · prompts + models · costs</div>
                <div className="rule-bottom" />
            </div>
            <nav style={adminNav}>
                <Link to="/admin"           style={navLink}>Overview</Link>
                <Link to="/admin/ai-agents" style={navLink}>AI Agents</Link>
                <Link to="/admin/newsroom"  style={navLink}>Newsroom</Link>
                <Link to="/admin/map"       style={navLink}>Map</Link>
                <Link to="/admin/claims"    style={navLink}>Claims</Link>
                <Link to="/admin/analytics" style={navLink}>Analytics</Link>
            </nav>

            {cost && <CostPanel cost={cost} api={api} onChange={load} />}

            <section style={{ marginTop: 24 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h2>Agents ({agents.length})</h2>
                    <button type="button" className="plain" onClick={() => setSelected('new')}>+ new agent</button>
                </div>
                {agents.length === 0 ? (
                    <p style={{ color: '#666', fontStyle: 'italic' }}>No agents configured yet.</p>
                ) : (
                    <table style={tableStyle}>
                        <thead>
                            <tr style={{ borderBottom: '1px solid #cfc9b7', textAlign: 'left' }}>
                                <th style={th}>Name</th><th style={th}>Role</th><th style={th}>Community</th><th style={th}>Model</th>
                                <th style={th}>Style</th><th style={th}>Active</th>
                                <th style={th} title="Heartbeat — loop alive. Use the activity view for 'last action'.">Heartbeat</th>
                                <th style={th}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {agents.map(a => (
                                <tr key={a.ID} style={{ borderBottom: '1px solid #eee' }}>
                                    <td style={td}>{a.Name}</td>
                                    <td style={td}><code style={{ fontSize: 11 }}>{a.Role || '?'}</code></td>
                                    <td style={td}>{a.CommunitySlug}</td>
                                    <td style={td}>{a.Model.replace('claude-','').replace('-20251001','')}</td>
                                    <td style={td}>{a.ApproveStyle}</td>
                                    <td style={td}>
                                        {a.Active ? '✓' : '✗'}
                                        {a.PausedReason && <em style={{ color: '#8b1919', fontSize: 11 }}> ({a.PausedReason})</em>}
                                    </td>
                                    <td style={td}>{a.LastPolledAt ? new Date(a.LastPolledAt).toLocaleString() : '—'}</td>
                                    <td style={td}>
                                        <button type="button" className="plain" onClick={() => setActivityFor(a.ID)}>activity</button>
                                        {' · '}
                                        <button type="button" className="plain" onClick={async () => {
                                            try {
                                                await api.post(`/api/admin/ai-agents/${a.ID}/poll-now`, {});
                                                setActivityFor(a.ID);
                                            } catch (e) { alert('poll-now failed: ' + (e as Error).message); }
                                        }}>poll now</button>
                                        {' · '}
                                        <button type="button" className="plain" onClick={() => setSelected(a.ID)}>edit</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </section>

            {selected === 'new' && (
                <AgentForm mode="create" onDone={() => { setSelected(null); load(); }} onCancel={() => setSelected(null)} api={api} />
            )}
            {selectedAgent && (
                <AgentForm mode="edit" agent={selectedAgent} onDone={() => { setSelected(null); load(); }} onCancel={() => setSelected(null)} api={api} />
            )}
            {activityAgent && (
                <div>
                    <div style={{ marginTop: 20, fontSize: 12, color: '#666' }}>
                        <button type="button" className="plain" onClick={() => setActivityFor(null)}>× close</button>
                        {' · '}Activity for <strong>{activityAgent.Name}</strong> ({activityAgent.CommunitySlug})
                    </div>
                    <ActivityFeed agentID={activityAgent.ID} api={api} />
                </div>
            )}
            {selectedAgent && <ActivityFeed agentID={selectedAgent.ID} api={api} />}
            {selectedAgent && <DecisionHistory agentID={selectedAgent.ID} api={api} />}
        </main>
    );
}

// ---- Cost panel ----

function CostPanel({ cost, api, onChange }: { cost: CostSummary; api: ReturnType<typeof useApi>; onChange: () => void }) {
    const [editingCap, setEditingCap] = useState(false);
    const [daily, setDaily] = useState(cost.global.cap_daily);
    const [monthly, setMonthly] = useState(cost.global.cap_monthly);

    async function save() {
        try {
            await api.post('/api/admin/ai-cost/caps', {
                scope: 'global', daily_cents: daily, monthly_cents: monthly,
            });
            setEditingCap(false); onChange();
        } catch (e) { alert('save failed: ' + (e as Error).message); }
    }

    return (
        <section style={{ marginTop: 24 }}>
            <h2>Spend</h2>
            <div style={cardsGrid}>
                <Card label="Global · today"       value={`${(cost.global.daily_cents / 100).toFixed(2)} / ${(cost.global.cap_daily / 100).toFixed(2)}`} />
                <Card label="Global · 30 days"     value={`${(cost.global.monthly_cents / 100).toFixed(2)} / ${(cost.global.cap_monthly / 100).toFixed(2)}`} />
                <Card label="Agents active"        value={cost.agents.filter(a => a.active).length} />
                <Card label="Agents paused"        value={cost.agents.filter(a => !a.active || a.paused_reason).length} />
            </div>
            <div style={{ marginTop: 8 }}>
                {!editingCap ? (
                    <button type="button" className="plain" onClick={() => setEditingCap(true)}>edit global caps</button>
                ) : (
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <label style={{ fontSize: 13 }}>Daily cents: <input type="number" value={daily} onChange={e => setDaily(Number(e.target.value))} style={inputStyle} /></label>
                        <label style={{ fontSize: 13 }}>Monthly cents: <input type="number" value={monthly} onChange={e => setMonthly(Number(e.target.value))} style={inputStyle} /></label>
                        <button type="button" className="primary" onClick={save} style={btn}>save</button>
                        <button type="button" className="plain" onClick={() => setEditingCap(false)}>cancel</button>
                    </div>
                )}
            </div>
            {cost.agents.length > 0 && (
                <table style={tableStyle}>
                    <thead>
                        <tr style={{ borderBottom: '1px solid #cfc9b7', textAlign: 'left' }}>
                            <th style={th}>Agent</th><th style={th}>Community</th>
                            <th style={th}>Today</th><th style={th}>30d</th><th style={th}>Cap (scope)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {cost.agents.map(a => (
                            <tr key={a.agent_id} style={{ borderBottom: '1px solid #eee' }}>
                                <td style={td}>{a.agent_name}</td>
                                <td style={td}>{a.community}</td>
                                <td style={td}>{(a.daily_cents / 100).toFixed(2)}</td>
                                <td style={td}>{(a.monthly_cents / 100).toFixed(2)}</td>
                                <td style={td}>{(a.cap_daily / 100).toFixed(2)}d / {(a.cap_monthly / 100).toFixed(2)}m ({a.cap_scope})</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </section>
    );
}

// ---- Agent form (create + edit shared) ----

interface FormProps {
    mode:     'create' | 'edit';
    agent?:   Agent;
    onDone:   () => void;
    onCancel: () => void;
    api:      ReturnType<typeof useApi>;
}

interface StaffUser { id: string; display_name: string; email: string; keycloak_sub: string }
interface CommunityLite { id: string; slug: string; name: string }

function AgentForm({ mode, agent, onDone, onCancel, api }: FormProps) {
    const [name, setName]             = useState(agent?.Name ?? '');
    const [kcUser, setKCUser]         = useState(agent?.KCUsername ?? '');
    const [kcPass, setKCPass]         = useState('');   // never populated from GET (redacted)
    const [onhereUser, setOnhereUser] = useState(agent?.OnHereUserID ?? '');
    const [community, setCommunity]   = useState(agent?.CommunityID ?? '');

    // Populate dropdowns on mount. Community list uses the existing public
    // /api/communities endpoint; user list uses the staff-only /api/admin/users.
    const [users, setUsers] = useState<StaffUser[]>([]);
    const [communities, setCommunities] = useState<CommunityLite[]>([]);
    useEffect(() => {
        api.get<{ users: StaffUser[] }>('/api/admin/users').then(r => setUsers(r.users ?? [])).catch(() => {});
        api.get<{ communities: CommunityLite[] }>('/api/communities').then(r => setCommunities(r.communities ?? [])).catch(() => {});
    }, [api]);

    // When staff picks a user in the dropdown, auto-fill kc_username with
    // their email (KC users' username = email in our setup). Staff still
    // types the password since we can't fetch it from KC.
    function pickUser(userID: string) {
        setOnhereUser(userID);
        const u = users.find(x => x.id === userID);
        if (u) setKCUser(u.email);
    }
    const [role, setRole]             = useState<'editor'|'reporter'|'writer'|'curator'|'weather'|'feed_writer'>(agent?.Role ?? 'editor');
    const [systemPrompt, setSystemPrompt] = useState(agent?.SystemPrompt ?? DEFAULT_SYSTEM_PROMPT);
    const [commentPrompt, setCommentPrompt] = useState(agent?.CommentPrompt ?? '');
    const [approveStyle, setApproveStyle] = useState<'suggest_only' | 'auto'>(agent?.ApproveStyle ?? 'suggest_only');
    const [moderateComments, setModerateComments] = useState(agent?.ModerateComments ?? false);
    const [model, setModel]           = useState(agent?.Model ?? 'claude-haiku-4-5-20251001');
    const [maxTokens, setMaxTokens]   = useState(agent?.MaxTokens ?? 1024);
    const [temperature, setTemperature] = useState(agent?.Temperature ?? 0.2);
    const [pollS, setPollS]           = useState(agent?.PollIntervalS ?? 60);
    const [active, setActive]         = useState(agent?.Active ?? true);
    const [status, setStatus]         = useState('');

    async function save() {
        setStatus('saving…');
        try {
            if (mode === 'create') {
                await api.post('/api/admin/ai-agents', {
                    name, kc_username: kcUser, kc_password: kcPass,
                    onhere_user_id: onhereUser, community_id: community,
                    system_prompt: systemPrompt, comment_prompt: commentPrompt || null,
                    approve_style: approveStyle, moderate_comments: moderateComments,
                    model, max_tokens: maxTokens, temperature, poll_interval_s: pollS,
                    role,
                });
            } else if (agent) {
                await api.patch(`/api/admin/ai-agents/${agent.ID}`, {
                    system_prompt: systemPrompt,
                    comment_prompt: commentPrompt || null,
                    approve_style: approveStyle,
                    moderate_comments: moderateComments,
                    model, max_tokens: maxTokens, temperature, poll_interval_s: pollS,
                    active,
                    // Blank = leave unchanged. Only sent if user typed a new value.
                    kc_username: kcUser && kcUser !== agent.KCUsername ? kcUser : null,
                    kc_password: kcPass || null,
                });
            }
            onDone();
        } catch (e) { setStatus('failed: ' + (e as Error).message); }
    }

    return (
        <section style={{ marginTop: 24, padding: 16, border: '1px solid #cfc9b7', background: '#faf8f2' }}>
            <h2>{mode === 'create' ? 'New agent' : 'Edit ' + agent?.Name}</h2>
            <div style={grid}>
                {mode === 'create' && <>
                    <Field label="Role">
                        <select style={inputStyle} value={role} onChange={e => setRole(e.target.value as typeof role)}>
                            <option value="editor">Editor — moderates the community's submission queue (approve/reject/return)</option>
                            <option value="reporter">Reporter Scout — watches feeds, extracts fact packs, drafts outreach</option>
                            <option value="writer">Writer — publishes stories from Reporter fact packs</option>
                            <option value="curator">Curator — daily whole-paper pass (lead + section order + tone)</option>
                            <option value="weather">Weather — pulls Yr.no forecast + writes the paper's weather blurb</option>
                            <option value="feed_writer">Feed writer — reads external feeds (X/RSS), drafts posts into the community's queue for editor approval</option>
                        </select>
                    </Field>
                    <Field label="Name (e.g. Vredehoek Reporter)">
                        <input style={inputStyle} value={name} onChange={e => setName(e.target.value)} />
                    </Field>
                    <Field label="OnHere user (must already have a KC login)">
                        <select style={inputStyle} value={onhereUser} onChange={e => pickUser(e.target.value)}>
                            <option value="">— pick a user —</option>
                            {users.map(u => (
                                <option key={u.id} value={u.id}>
                                    {u.display_name} · {u.email}
                                </option>
                            ))}
                        </select>
                    </Field>
                    {(role === 'editor' || role === 'writer' || role === 'feed_writer') && <>
                        <Field label="KC username (auto-filled from user; edit if different)">
                            <input style={inputStyle} value={kcUser} onChange={e => setKCUser(e.target.value)} />
                        </Field>
                        <Field label="KC password (staff must know this — not stored anywhere else)">
                            <input style={inputStyle} type="password" value={kcPass} onChange={e => setKCPass(e.target.value)} />
                        </Field>
                    </>}
                    {(role === 'reporter' || role === 'curator' || role === 'weather') && (
                        <Field label="KC credentials">
                            <span style={{ fontSize: 12, color: '#666', fontStyle: 'italic' }}>
                                Not needed — {role}s don't post through the OnHere API. Server fills placeholders.
                            </span>
                        </Field>
                    )}
                    <Field label="Community this agent works in">
                        <select style={inputStyle} value={community} onChange={e => setCommunity(e.target.value)}>
                            <option value="">— pick a community —</option>
                            {communities.map(c => (
                                <option key={c.id} value={c.id}>
                                    {c.name} · {c.slug}
                                </option>
                            ))}
                        </select>
                    </Field>
                </>}
                <Field label="Model">
                    <select style={inputStyle} value={model} onChange={e => setModel(e.target.value)}>
                        <option value="claude-haiku-4-5-20251001">Haiku 4.5 (cheap, fast — default)</option>
                        <option value="claude-sonnet-4-6">Sonnet 4.6 (balanced)</option>
                        <option value="claude-opus-4-7">Opus 4.7 (best judgment, expensive)</option>
                    </select>
                </Field>
                <Field label="Approve style">
                    <select style={inputStyle} value={approveStyle} onChange={e => setApproveStyle(e.target.value as 'suggest_only' | 'auto')}>
                        <option value="suggest_only">Suggest only — human editor still sees queue with AI reasoning</option>
                        <option value="auto">Auto — AI approves/rejects directly (trust required)</option>
                    </select>
                </Field>
                <Field label="Max tokens per call"><input style={inputStyle} type="number" value={maxTokens} onChange={e => setMaxTokens(Number(e.target.value))} /></Field>
                <Field label="Temperature (0–1)"><input style={inputStyle} type="number" step="0.05" value={temperature} onChange={e => setTemperature(Number(e.target.value))} /></Field>
                <Field label="Poll interval (seconds)"><input style={inputStyle} type="number" value={pollS} onChange={e => setPollS(Number(e.target.value))} /></Field>
                <Field label="Moderate comments">
                    <input type="checkbox" checked={moderateComments} onChange={e => setModerateComments(e.target.checked)} />
                </Field>
                {mode === 'edit' && <Field label="Active">
                    <input type="checkbox" checked={active} onChange={e => setActive(e.target.checked)} />
                </Field>}
                {mode === 'edit' && <>
                    <Field label={`KC username (role: ${agent?.Role || '?'})`}>
                        <input style={inputStyle} value={kcUser} onChange={e => setKCUser(e.target.value)} placeholder={agent?.KCUsername || '(none set)'} />
                    </Field>
                    <Field label="KC password (leave blank to keep existing; type new to rotate)">
                        <input style={inputStyle} type="password" value={kcPass} onChange={e => setKCPass(e.target.value)} placeholder="•••••" />
                    </Field>
                </>}
            </div>
            <Field label="System prompt (personality + editorial rules)">
                <textarea style={textareaStyle} rows={12} value={systemPrompt} onChange={e => setSystemPrompt(e.target.value)} />
            </Field>
            <Field label="Comment prompt (leave empty to disable comment moderation)">
                <textarea style={textareaStyle} rows={6} value={commentPrompt} onChange={e => setCommentPrompt(e.target.value)} />
            </Field>
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button type="button" className="primary" onClick={save} style={btn}>save</button>
                <button type="button" className="plain" onClick={onCancel}>cancel</button>
                <span style={{ fontSize: 12, color: status.startsWith('failed') ? '#8b1919' : '#666' }}>{status}</span>
            </div>
        </section>
    );
}

// ---- Decision history ----

// ---- Activity feed ----
//
// Merged debug view across cost ledger, triaged feed items, posts
// authored. First place to look when an agent "isn't doing anything" —
// shows both wins (published) and quiet spend (cost with nothing to
// show for it).

interface ActivityEvent {
    time:            string;
    kind:            'cost' | 'published' | 'triaged';
    detail:          string;
    cost_zar_cents?: number | null;
    tokens_in?:      number | null;
    tokens_out?:     number | null;
    post_id?:        string | null;
    link?:           string | null;
}

function ActivityFeed({ agentID, api }: { agentID: string; api: ReturnType<typeof useApi> }) {
    const [events, setEvents] = useState<ActivityEvent[]>([]);
    const [pausedReason, setPausedReason] = useState<string | null>(null);
    const [lastPolled, setLastPolled] = useState<string | null>(null);
    const [lastAction, setLastAction] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get<{
                events: ActivityEvent[];
                paused_reason?: string | null;
                last_polled_at?: string | null;
                last_action_at?: string | null;
            }>(`/api/admin/ai-agents/${agentID}/activity?limit=60`);
            setEvents(r.events ?? []);
            setPausedReason(r.paused_reason ?? null);
            setLastPolled(r.last_polled_at ?? null);
            setLastAction(r.last_action_at ?? null);
        } catch {
            setEvents([]);
        } finally {
            setLoading(false);
        }
    }, [api, agentID]);
    useEffect(() => { load(); }, [load]);

    return (
        <section style={{ marginTop: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2>Activity ({events.length})</h2>
                <button type="button" className="plain" onClick={load}>refresh</button>
            </div>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>
                <span title="Loop heartbeat — proves the agent's goroutine is alive.">
                    Heartbeat: {lastPolled ? new Date(lastPolled).toLocaleString() : '— never'}
                </span>
                <span style={{ marginLeft: 12 }}
                    title="Last time the agent actually did work (published, updated, cost, or triaged an item).">
                    Last action: {lastAction ? new Date(lastAction).toLocaleString() : '— never'}
                </span>
                {pausedReason && <span style={{ color: '#8b1919', marginLeft: 12 }}>paused: {pausedReason}</span>}
            </div>
            {loading ? (
                <p style={{ color: '#666', fontStyle: 'italic' }}>Loading…</p>
            ) : events.length === 0 ? (
                <p style={{ color: '#666', fontStyle: 'italic' }}>
                    No activity yet. Agent may not have run its first tick, or has nothing to process.
                </p>
            ) : (
                <table style={tableStyle}>
                    <thead>
                        <tr style={{ borderBottom: '1px solid #cfc9b7', textAlign: 'left' }}>
                            <th style={th}>When</th>
                            <th style={th}>Kind</th>
                            <th style={th}>Detail</th>
                            <th style={th}>Cost (c)</th>
                            <th style={th}>Tokens</th>
                        </tr>
                    </thead>
                    <tbody>
                        {events.map((e, i) => (
                            <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                                <td style={td}>{new Date(e.time).toLocaleString()}</td>
                                <td style={td}>
                                    <span style={kindBadge(e.kind)}>{e.kind}</span>
                                </td>
                                <td style={{ ...td, maxWidth: 500 }}>
                                    {e.detail}
                                    {e.link && (
                                        <> · <a href={e.link} target="_blank" rel="noreferrer" style={{ color: '#555' }}>src</a></>
                                    )}
                                    {e.post_id && (
                                        <> · <Link to={`/posts/${e.post_id}`} style={{ color: '#555' }}>post</Link></>
                                    )}
                                </td>
                                <td style={td}>{e.cost_zar_cents != null ? e.cost_zar_cents.toFixed(3) : '—'}</td>
                                <td style={td}>
                                    {e.tokens_in != null || e.tokens_out != null
                                        ? `${e.tokens_in ?? 0}+${e.tokens_out ?? 0}`
                                        : '—'}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </section>
    );
}

function kindBadge(kind: string): React.CSSProperties {
    const base: React.CSSProperties = {
        fontFamily: 'monospace', fontSize: 10, padding: '1px 6px',
        border: '1px solid #cfc9b7', textTransform: 'uppercase', letterSpacing: 1,
    };
    // Green = something got shipped, yellow = judged & skipped, red = error.
    if (kind === 'published' || kind === 'updated')
        return { ...base, background: '#e6f0e0', borderColor: '#88a877', color: '#3a5c2b' };
    if (kind === 'skipped_duplicate' || kind === 'skipped_not_relevant' || kind === 'skipped_too_old')
        return { ...base, background: '#fbf3d9', borderColor: '#c9b877', color: '#7a6817' };
    if (kind === 'parse_error')
        return { ...base, background: '#f8d7d7', borderColor: '#b06060', color: '#8b1919' };
    if (kind === 'cost')
        return { ...base, background: '#f2eee0', color: '#555' };
    if (kind === 'triaged')
        return { ...base, background: '#fbf3d9', borderColor: '#c9b877' };
    return { ...base, background: '#f2eee0' };
}

function DecisionHistory({ agentID, api }: { agentID: string; api: ReturnType<typeof useApi> }) {
    const [decisions, setDecisions] = useState<Decision[]>([]);
    useEffect(() => {
        api.get<{ decisions: Decision[] }>(`/api/admin/ai-agents/${agentID}/decisions?limit=30`)
            .then(r => setDecisions(r.decisions ?? []))
            .catch(() => setDecisions([]));
    }, [api, agentID]);
    return (
        <section style={{ marginTop: 24 }}>
            <h2>Recent decisions ({decisions.length})</h2>
            {decisions.length === 0 ? (
                <p style={{ color: '#666', fontStyle: 'italic' }}>No decisions yet.</p>
            ) : (
                <table style={tableStyle}>
                    <thead>
                        <tr style={{ borderBottom: '1px solid #cfc9b7', textAlign: 'left' }}>
                            <th style={th}>When</th><th style={th}>Kind</th><th style={th}>Executed</th>
                            <th style={th}>Cost</th><th style={th}>Tokens</th><th style={th}>Reasoning</th>
                        </tr>
                    </thead>
                    <tbody>
                        {decisions.map(d => (
                            <tr key={d.ID} style={{ borderBottom: '1px solid #eee' }}>
                                <td style={td}>{new Date(d.CreatedAt).toLocaleString()}</td>
                                <td style={td}>{d.DecisionKind}</td>
                                <td style={td}>{d.Executed ? '✓' : '—'}</td>
                                <td style={td}>{(d.CostCents / 100).toFixed(3)}</td>
                                <td style={td}>{d.TokensIn}+{d.TokensOut}</td>
                                <td style={{ ...td, maxWidth: 400 }}>{d.Reasoning.slice(0, 200)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </section>
    );
}

// ---- Shared components + styles ----

function Field({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <label style={{ display: 'block', marginTop: 8 }}>
            <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: '#666', marginBottom: 2 }}>{label}</div>
            {children}
        </label>
    );
}
function Card({ label, value }: { label: string; value: number | string }) {
    return (
        <div style={{ padding: '10px 12px', border: '1px solid #cfc9b7', background: '#faf8f2' }}>
            <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, color: '#666' }}>{label}</div>
            <div style={{ fontSize: 20, fontFamily: "'Newsreader', serif" }}>{value}</div>
        </div>
    );
}
const grid: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 };
const cardsGrid: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 8, marginTop: 8 };
const tableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontFamily: '-apple-system, sans-serif', fontSize: 12, marginTop: 12 };
const th: React.CSSProperties = { padding: '6px 8px' };
const td: React.CSSProperties = { padding: '6px 8px' };
const inputStyle: React.CSSProperties = { padding: '4px 8px', fontFamily: 'monospace', fontSize: 12, width: '100%' };
const textareaStyle: React.CSSProperties = { padding: 8, fontFamily: 'monospace', fontSize: 12, width: '100%', border: '1px solid #cfc9b7' };
const btn: React.CSSProperties = { padding: '6px 16px' };
const adminNav: React.CSSProperties = {
    display: 'flex', gap: 12, padding: '10px 0 6px', marginBottom: 16,
    borderBottom: '1px solid #cfc9b7',
    fontFamily: '-apple-system, sans-serif',
    fontSize: 11, textTransform: 'uppercase', letterSpacing: 2,
};
const navLink: React.CSSProperties = {
    color: '#111', textDecoration: 'none', padding: '4px 10px',
    border: '1px solid transparent', fontWeight: 700,
};

const DEFAULT_SYSTEM_PROMPT = `You are the editor for the OnHere community for {community_name}, a hyperlocal neighbourhood newspaper. Your role is to evaluate submitted posts and decide whether they should be published, rejected, or returned to the author for revision.

Approve when: the post is genuinely relevant to residents of this community, factually plausible, respectful in tone, and adds value (local news, events, discussion, useful notices).

Reject when: the post is spam, an obvious scam, hate speech, direct incitement to violence, or completely unrelated to the community (e.g. bitcoin sales pitches, adult content).

Return for revision when: the post has editorial merit but needs work — missing crucial detail (e.g. an event with no date), unverified claims that need sourcing, unclear ask.

Respond with ONLY a JSON object exactly matching this shape:
{"decision": "approve" | "reject" | "return", "reason": "one paragraph, max 300 chars, explaining your decision in a way the author or a human editor can understand"}

No preamble. No markdown fencing. Just the JSON.`;
